/**
 * Registre des LIEUX de transfert (origines / destinations).
 *
 * Rôle : source unique des libellés, slugs et types de lieu utilisés par les
 * pages "transfert A → B" (Compact SEO bas-de-funnel).
 *
 * Dépendances :
 * - Les IDs ci-dessous DOIVENT correspondre aux clés utilisées dans
 *   `VEHICLES[].fixedTransfers` de `app/config/infos.ts` (ex. "CANNES",
 *   "NCE_AIRPORT"). C'est ce qui permet de récupérer les prix réels sans les
 *   dupliquer (cf. `app/data/transfers/index.ts` → getRoutePrices()).
 * - Consommé par `routes.ts` (définition des trajets) et les composants de
 *   `app/(global)/chauffeur/transferts/`.
 *
 * ⚠️ Ne jamais inventer un prix ici. Les prix vivent UNIQUEMENT dans infos.ts.
 */
import type { Locale } from "@/config/i18n";

export type TransferPlaceType = "city" | "airport" | "village";

export type TransferPlace = {
  /** ID interne — identique à la clé utilisée dans VEHICLES.fixedTransfers. */
  id: string;
  /** Nom complet pour les phrases ("vers l'aéroport de Nice"). */
  name: string;
  /** Nom court pour titres/tableaux ("Aéroport de Nice"). */
  shortName: string;
  /** Forme utilisée dans les slugs d'URL ("aeroport-nice"). */
  slugPart: string;
  type: TransferPlaceType;
  /** Surcharges EN (optionnelles ; fallback FR si absent). */
  nameEn?: string;
  shortNameEn?: string;
  /**
   * Surcharge AR : les graphies sont celles que `routesAr.ts` écrit déjà dans
   * ses FAQ (« كان », « مطار نيس »…). Sans elle, une phrase arabe générée
   * recevait le nom latin au milieu du texte.
   */
  shortNameAr?: string;
};

export const TRANSFER_PLACES: Record<string, TransferPlace> = {
  CANNES: {
    id: "CANNES",
    name: "Cannes",
    shortName: "Cannes",
    slugPart: "cannes",
    type: "city",
    shortNameAr: "كان",
  },
  NCE_AIRPORT: {
    id: "NCE_AIRPORT",
    name: "l'aéroport de Nice",
    shortName: "Aéroport de Nice",
    slugPart: "aeroport-nice",
    type: "airport",
    nameEn: "Nice Airport",
    shortNameEn: "Nice Airport",
    shortNameAr: "مطار نيس",
  },
  MONACO: {
    id: "MONACO",
    name: "Monaco",
    shortName: "Monaco",
    slugPart: "monaco",
    type: "city",
    shortNameAr: "موناكو",
  },
  SAINT_TROPEZ: {
    id: "SAINT_TROPEZ",
    name: "Saint-Tropez",
    shortName: "Saint-Tropez",
    slugPart: "saint-tropez",
    type: "city",
    shortNameAr: "سان تروبيه",
  },
  ST_JEAN_CAP_FERRAT: {
    id: "ST_JEAN_CAP_FERRAT",
    name: "Saint-Jean-Cap-Ferrat",
    shortName: "Saint-Jean-Cap-Ferrat",
    slugPart: "saint-jean-cap-ferrat",
    type: "village",
    shortNameAr: "سان جان كاب فيرا",
  },
};

/** Nom court d'un lieu selon la locale (fallback FR). */
export const placeShortName = (
  place: TransferPlace,
  locale: Locale,
): string => {
  if (locale === "en") return place.shortNameEn ?? place.shortName;
  if (locale === "ar") return place.shortNameAr ?? place.shortName;
  return place.shortName;
};

/** Nom complet d'un lieu selon la locale (fallback FR). */
export const placeName = (place: TransferPlace, locale: Locale): string =>
  locale === "en" ? (place.nameEn ?? place.name) : place.name;

/** Récupère un lieu par ID, lève une erreur explicite si introuvable (fail-fast au build). */
export const getTransferPlace = (id: string): TransferPlace => {
  const place = TRANSFER_PLACES[id];
  if (!place) {
    throw new Error(
      `[transfers] Lieu inconnu "${id}". Vérifier TRANSFER_PLACES dans app/data/transfers/places.ts.`,
    );
  }
  return place;
};
